/**
 * useBridgeFlow — state machine for the Add ETH bridge flow.
 *
 * Manages the full lifecycle:
 *   idle → loading_config → preparing → awaiting_approval
 *        → submitted_l1 → confirming_l1 → depositing_l2 → ready
 *
 * Error paths: failed | manual_redemption
 *
 * Security invariants:
 *   - Bridge config is fetched from the server; addresses are never supplied
 *     by the caller or read from the browser.
 *   - No private key is accessed.
 *   - No SYNAPZ custody wallet is used.
 *   - The wallet's L1 ETH gas balance is checked before submission.
 *
 * Deposit mechanism:
 *   1. Switch wallet to L1 chain (via EIP-1193 wallet_switchEthereumChain).
 *   2. Call depositEth() on the Arbitrum Inbox contract with the desired value.
 *      The function signature is: depositEth() payable returns (uint256).
 *      Selector: 0x439370b1
 *   3. Switch wallet back to Robinhood Chain.
 *   4. Poll L1 receipt (via public Ethereum RPC) and L2 receipt (via Robinhood RPC).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPublicClient, http, formatEther } from "viem";
import {
  type BridgeChainConfig,
  type BridgeStatus,
  fetchBridgeConfig,
  persistBridgeTx,
  fetchPendingBridgeTx,
} from "../config/bridgeConfig";
import { ACTIVE_CHAIN } from "../config/chains";

// ── Etherscan-compatible L1 RPC URLs ──────────────────────────────────────────
// Used for gas estimation and L1 tx monitoring.  The user's wallet handles
// the actual L1 signature — no private key is used here.

function l1RpcUrl(l1ChainId: number): string {
  // Testnet: Ethereum Sepolia public RPC
  if (l1ChainId === 11155111) return "https://ethereum-sepolia-rpc.publicnode.com";
  // Mainnet: Ethereum mainnet public RPC
  if (l1ChainId === 1) return "https://ethereum-rpc.publicnode.com";
  throw new Error(`No public RPC configured for L1 chain ${l1ChainId}`);
}

// ── Types ──────────────────────────────────────────────────────────────────────

export interface BridgeFlowState {
  status: BridgeStatus;
  config: BridgeChainConfig | null;
  configError: string | null;
  /** Amount to bridge (wei), controlled by the component */
  amountWei: bigint | null;
  /** L1 gas estimate for the deposit transaction */
  l1GasEstimate: bigint | null;
  /** L1 gas price in wei */
  l1GasPrice: bigint | null;
  /** L1 tx hash, populated after submission */
  l1TxHash: `0x${string}` | null;
  /** Number of L1 confirmations received */
  l1Confirmations: number;
  /** Error message for display */
  error: string | null;
  /** Whether the user's L1 ETH balance is sufficient for gas */
  l1GasWarning: boolean;
  /** Estimated available L2 balance after bridge completes */
  estimatedL2Balance: bigint | null;
}

export interface UseBridgeFlowOptions {
  wallet: string | null;
  destinationChainId: number;
  currentL2Balance: bigint | null;
  /** Shortfall to prefill in the amount input */
  shortfallWei: bigint | null;
  returnContext: "trade" | "create" | null;
  onReady: () => void;
}

export interface UseBridgeFlowResult {
  state: BridgeFlowState;
  amountInput: string;
  setAmountInput: (v: string) => void;
  canSubmit: boolean;
  submit: () => Promise<void>;
  dismiss: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useBridgeFlow({
  wallet,
  destinationChainId,
  currentL2Balance,
  shortfallWei,
  returnContext,
  onReady,
}: UseBridgeFlowOptions): UseBridgeFlowResult {
  const [state, setState] = useState<BridgeFlowState>({
    status: "idle",
    config: null,
    configError: null,
    amountWei: null,
    l1GasEstimate: null,
    l1GasPrice: null,
    l1TxHash: null,
    l1Confirmations: 0,
    error: null,
    l1GasWarning: false,
    estimatedL2Balance: null,
  });

  // Amount input is a controlled string; amountWei is derived from it
  const [amountInput, setAmountInputRaw] = useState<string>("");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // ── Load config on mount ───────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!wallet) return;
    setState(s => ({ ...s, status: "loading_config", configError: null }));

    fetchBridgeConfig(destinationChainId).then(config => {
      if (!mountedRef.current) return;
      setState(s => ({ ...s, status: "preparing", config }));

      // Prefill amount from shortfall
      if (shortfallWei !== null && shortfallWei > 0n) {
        const shortfallEth = formatEther(shortfallWei);
        // Round up to 2 decimal places and add 10% buffer for gas on destination
        const buffered = (Number(shortfallEth) * 1.1).toFixed(4);
        setAmountInputRaw(buffered);
      }

      // Check for in-progress bridge to resume
      void fetchPendingBridgeTx(wallet, destinationChainId).then(pending => {
        if (!mountedRef.current || !pending) return;
        // Resume in-progress bridge
        setState(s => ({
          ...s,
          status: pending.status,
          l1TxHash: pending.l1TxHash,
          amountWei: BigInt(pending.amountWei),
        }));
        if (pending.l1TxHash && !["ready", "failed"].includes(pending.status)) {
          void pollStatus(config, pending.l1TxHash, pending.amountWei);
        }
      });
    }).catch(err => {
      if (!mountedRef.current) return;
      const msg = err instanceof Error ? err.message : "Bridge configuration unavailable";
      setState(s => ({ ...s, status: "preparing", configError: msg }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, destinationChainId]);

  // ── Estimate L1 gas when amount changes ────────────────────────────────────

  const amountWei = (() => {
    const n = Number(amountInput);
    if (!amountInput || isNaN(n) || n <= 0) return null;
    try {
      return BigInt(Math.floor(n * 1e18));
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const config = state.config;
    if (!config || !amountWei || amountWei <= 0n) {
      setState(s => ({ ...s, l1GasEstimate: null, l1GasPrice: null, l1GasWarning: false }));
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const l1Client = createPublicClient({
          transport: http(l1RpcUrl(config.l1ChainId)),
        });

        const [gasPrice, gasEstimate] = await Promise.all([
          l1Client.getGasPrice(),
          l1Client.estimateGas({
            to: config.inboxAddress,
            value: amountWei,
            data: "0x439370b1", // depositEth()
            account: (wallet ?? "0x0000000000000000000000000000000000000000") as `0x${string}`,
          }).catch(() => 200_000n), // fallback estimate
        ]);

        if (cancelled || !mountedRef.current) return;

        // Check if user would be left without gas on L1
        // We can't read L1 balance without an L1 RPC call — we make a best-effort estimate.
        // A more accurate check would require the user's L1 ETH balance, which
        // we don't have directly. We show a warning instead of blocking.
        const gasCost = gasPrice * gasEstimate;

        setState(s => ({
          ...s,
          l1GasEstimate: gasEstimate,
          l1GasPrice: gasPrice,
          l1GasWarning: gasCost > amountWei / 10n, // warn if gas > 10% of amount
          estimatedL2Balance: (currentL2Balance ?? 0n) + amountWei,
        }));
      } catch {
        if (!cancelled && mountedRef.current) {
          setState(s => ({ ...s, l1GasEstimate: null, l1GasPrice: null }));
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountInput, state.config, wallet]);

  // ── L1 + L2 polling ────────────────────────────────────────────────────────

  const pollStatus = useCallback(async (
    config: BridgeChainConfig,
    l1TxHash: `0x${string}`,
    amountWeiStr: string,
  ) => {
    if (!mountedRef.current) return;

    const l1Client = createPublicClient({
      transport: http(l1RpcUrl(config.l1ChainId)),
    });
    const l2Client = createPublicClient({
      transport: http(ACTIVE_CHAIN.rpcUrl),
    });

    const checkOnce = async () => {
      if (!mountedRef.current) return;

      try {
        const l1Receipt = await l1Client.getTransactionReceipt({ hash: l1TxHash }).catch(() => null);

        if (!l1Receipt) {
          // Still in mempool — but do NOT regress status if we are already further
          // along (e.g. resuming from depositing_l2 and the L1 RPC node is flaky).
          setState(s => {
            const alreadyAdvanced = (["confirming_l1", "depositing_l2", "ready"] as BridgeStatus[]).includes(s.status);
            return alreadyAdvanced ? s : { ...s, status: "submitted_l1", l1Confirmations: 0 };
          });
        } else if (l1Receipt.status === "reverted") {
          setState(s => ({ ...s, status: "failed", error: "The L1 deposit transaction was reverted. No ETH was bridged." }));
          return;
        } else {
          // L1 confirmed — check L2
          const confs = Number(l1Receipt.blockNumber);
          setState(s => ({
            ...s,
            status: "confirming_l1",
            l1Confirmations: confs > 0 ? 1 : 0,
          }));

          // Try to find the L2 receipt by scanning recent blocks for a deposit
          // with matching amount. In a production implementation, this would use
          // the Arbitrum retryable ticket tracker SDK.
          // For now we check the L2 balance to detect when funds have arrived.
          if (wallet) {
            const l2Balance = await l2Client
              .getBalance({ address: wallet as `0x${string}` })
              .catch(() => null);

            const expectedMin = (currentL2Balance ?? 0n) + BigInt(amountWeiStr) / 2n;
            if (l2Balance !== null && l2Balance >= expectedMin) {
              setState(s => ({
                ...s,
                status: "ready",
                estimatedL2Balance: l2Balance,
                error: null,
              }));
              onReady();
              return; // Stop polling
            } else {
              setState(s => ({ ...s, status: "depositing_l2" }));
            }
          }
        }
      } catch {
        // Polling failure is transient — keep polling
      }

      // Schedule next poll (15 seconds)
      if (mountedRef.current) {
        pollRef.current = setTimeout(() => void checkOnce(), 15_000);
      }
    };

    void checkOnce();
  }, [wallet, currentL2Balance, onReady]);

  // ── In-flight lock ──────────────────────────────────────────────────────────
  // Prevents double-submission on rapid double-tap. The lock is ref-based
  // (synchronous) rather than state-based (asynchronous) so it is effective
  // even before the first React render cycle completes after the first call.
  const bridgeInFlightRef = useRef(false);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const submit = useCallback(async () => {
    const { config } = state;
    if (bridgeInFlightRef.current || !config || !amountWei || !wallet) return;
    bridgeInFlightRef.current = true;

    const provider = typeof window !== "undefined" ? window.ethereum : undefined;
    if (!provider) {
      setState(s => ({ ...s, error: "No wallet detected. Install MetaMask or Rabby." }));
      return;
    }

    // Verify bridge config addresses match server response (not user-supplied)
    if (!/^0x[0-9a-fA-F]{40}$/.test(config.inboxAddress)) {
      setState(s => ({
        ...s,
        status: "failed",
        error: "Bridge address verification failed. Do not proceed.",
      }));
      return;
    }

    const txId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      // Step 1: Switch wallet to L1
      setState(s => ({ ...s, status: "awaiting_approval", error: null }));

      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${config.l1ChainId.toString(16)}` }],
      });

      // Step 2: Send depositEth() on L1
      // Verify current chain before sending
      const currentChainId = await provider.request({ method: "eth_chainId" }) as string;
      const currentChainIdNum = parseInt(currentChainId, 16);
      if (currentChainIdNum !== config.l1ChainId) {
        throw new Error(
          `Wallet is on chain ${currentChainIdNum}, expected L1 chain ${config.l1ChainId}. ` +
          "Please switch networks manually and try again.",
        );
      }

      // Verify destination wallet = connected wallet (no redirection)
      const accounts = await provider.request({ method: "eth_accounts" }) as string[];
      const currentAccount = accounts[0]?.toLowerCase();
      if (!currentAccount || currentAccount !== wallet.toLowerCase()) {
        throw new Error("Wallet account changed. Please reconnect and try again.");
      }

      const l1TxHash = await provider.request({
        method: "eth_sendTransaction",
        params: [{
          from: currentAccount,
          to: config.inboxAddress,
          value: `0x${amountWei.toString(16)}`,
          data: config.depositEthSelector, // depositEth()
        }],
      }) as `0x${string}`;

      setState(s => ({ ...s, status: "submitted_l1", l1TxHash }));

      // Persist to server
      void persistBridgeTx({
        id: txId,
        wallet,
        destinationChainId,
        l1ChainId: config.l1ChainId,
        l1TxHash,
        amountWei: amountWei.toString(),
        status: "submitted_l1",
        retryableTicketId: null,
        returnContext,
      });

      // Step 3: Switch wallet back to Robinhood Chain
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${destinationChainId.toString(16)}` }],
      }).catch(() => {
        // Non-fatal — user can switch manually
      });

      // Step 4: Start polling
      void pollStatus(config, l1TxHash, amountWei.toString());

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);

      // User rejected the signature
      const isRejection =
        msg.includes("User rejected") ||
        msg.includes("user rejected") ||
        msg.includes("4001") ||
        msg.includes("ACTION_REJECTED");

      setState(s => ({
        ...s,
        status: isRejection ? "preparing" : "failed",
        error: isRejection
          ? null
          : `Transaction failed: ${msg}`,
      }));

      // Attempt to switch back to Robinhood Chain even on failure
      if (!isRejection) {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${destinationChainId.toString(16)}` }],
        }).catch(() => {});
      }
    } finally {
      bridgeInFlightRef.current = false;
    }
  }, [state, amountWei, wallet, destinationChainId, returnContext, pollStatus]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const minAmountWei = state.config ? BigInt(state.config.minimumAmountWei) : 0n;
  const canSubmit =
    state.status === "preparing" &&
    state.config !== null &&
    state.configError === null &&
    amountWei !== null &&
    amountWei >= minAmountWei &&
    !state.l1GasWarning;

  const dismiss = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setState(s => ({ ...s, status: "idle" }));
  }, []);

  const setAmountInput = useCallback((v: string) => {
    setAmountInputRaw(v);
  }, []);

  return { state, amountInput, setAmountInput, canSubmit, submit, dismiss };
}
